import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import SearchUserProfileView from '@CommonViews/SearchUserProfileView';
import ConfirmInviteForm from '@CommonForms/ConfirmInviteForm';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import OrgService from '@Helpers/URLHelpers/OrgService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import UserSeatService from '@Helpers/URLHelpers/BillingService/UserSeatService';
import ErrorHandler from '@Root/ErrorHandler';
import { FiSearch, FiX, FiUsers, FiUserPlus, FiShoppingBag } from 'react-icons/fi';
import {
    HiOutlineUserGroup,
    HiOutlineSparkles,
    HiOutlineCreditCard,
    HiOutlineExclamationCircle,
    HiOutlineCheckCircle
} from 'react-icons/hi2';
import './InviteUsersView.scss';

export default function InviteUsersView(props) {
    const [loading, setLoading] = useState(false);
    const [userList, setUserList] = useState([]);
    const [orgsOfUsers, setOrgsOfUsers] = useState([]);
    const [teamsOfUsers, setTeamsOfUsers] = useState([]);
    const [showProfileView, setShowProfileView] = useState(false);
    const [showConfirmInviteForm, setShowConfirmInviteForm] = useState(false);
    const [profileId, setProfileId] = useState(0);

    const [isInvited] = useState([]);
    const [sentInvitations, setSentInvitations] = useState([]);

    const [showSearchUser, setShowSearchUser] = useState(true);
    const [inviteeId, setInviteeId] = useState();
    const [inviteeFullName, setInviteeFullName] = useState();

    const [seatDetails, setSeatDetails] = useState({});
    const [hasAvailableSeats, setHasAvailableSeats] = useState(true);
    const [isCheckingSeats, setIsCheckingSeats] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const userInfoService = new UserInfoService();
    const orgService = new OrgService();
    const teamService = new TeamService();
    const invitationService = new InvitationService();
    const userSeatService = new UserSeatService();
    const errorHandler = new ErrorHandler();

    const { activeUserId } = useReduxActiveUser();
    const { currentProject } = useReduxProject();

    const KZQ_ENABLE_BILLING = false;

    const renderUserData = async (keyword) => {
        try {
            let usersData = await userInfoService.searchUserByKeyword(keyword);
            usersData = JSON.parse(usersData.body);

            const users = [];

            for (let i = 0; i < usersData.length; i++) {
                if (usersData[i].id !== activeUserId) {
                    users.push(usersData[i]);
                }
            }

            const orgs = [];
            const teams = [];

            for (let i = 0; i < users.length; i++) {
                let userOrgs = await orgService.getOrgListForUser(users[i].id);
                userOrgs = JSON.parse(userOrgs.body);

                let userTeams = await teamService.getTeamListByUserId(users[i].id);
                userTeams = JSON.parse(userTeams.body);

                orgs.push({
                    userId: users[i].id,
                    orgs: userOrgs
                });

                teams.push({
                    userId: users[i].id,
                    teams: userTeams
                });
            }

            let invitations =
                await invitationService.getSentInvitationListForUser(activeUserId);

            invitations = JSON.parse(invitations.body);

            setUserList(users);
            setOrgsOfUsers(orgs);
            setTeamsOfUsers(teams);
            setSentInvitations(invitations || []);
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const renderUserSeatDetails = async () => {
        try {
            setLoading(true);
            setIsCheckingSeats(true);

            let response =
                await userSeatService.getUserSeatDetailsByPmUserAndFreeByOrgId(
                    props.org.id
                );

            const details = JSON.parse(response.body);

            if (props.org.paymentType === 'trialUnlimited') {
                details.available = -1;
            }

            setSeatDetails(details);

            const availableSeats = details.available;

            setHasAvailableSeats(
                availableSeats === -1 || availableSeats > 0
            );

            setCurrentPlan(
                props.org.currentPlan ||
                details.planName ||
                'No Plan'
            );
        } catch (err) {
            errorHandler.handleError(err);
            setHasAvailableSeats(false);
        } finally {
            setLoading(false);
            setIsCheckingSeats(false);
        }
    };

    const handleSearchQueryChange = (e) => {
        const value = DOMPurify.sanitize(e.target.value);
        setSearchQuery(value);
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setLoading(true);
        setShowSearchUser(false);

        try {
            await renderUserData(searchQuery.toLowerCase());
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const onSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleDisable = (userId) => {
        if (props.project === undefined) {
            for (let i = 0; i < orgsOfUsers.length; i++) {
                if (orgsOfUsers[i].userId === userId) {
                    for (let j = 0; j < orgsOfUsers[i].orgs.length; j++) {
                        if (
                            orgsOfUsers[i].orgs[j].id ===
                            props.org.id
                        ) {
                            return true;
                        }
                    }
                }
            }

            for (let i = 0; i < sentInvitations.length; i++) {
                if (
                    sentInvitations[i].inviteeId === userId &&
                    sentInvitations[i].orgId === props.org.id &&
                    sentInvitations[i].teamId === 0
                ) {
                    return true;
                }
            }

            return false;
        }

        for (let i = 0; i < teamsOfUsers.length; i++) {
            if (teamsOfUsers[i].userId === userId) {
                for (let j = 0; j < teamsOfUsers[i].teams.length; j++) {
                    if (
                        teamsOfUsers[i].teams[j].id ===
                        props.team.id
                    ) {
                        return true;
                    }
                }
            }
        }

        for (let i = 0; i < sentInvitations.length; i++) {
            if (
                sentInvitations[i].inviteeId === userId &&
                sentInvitations[i].orgId === props.org.id &&
                sentInvitations[i].teamId === props.team.id
            ) {
                return true;
            }
        }

        return false;
    };

    const handleInvite = (user) => {
        setInviteeId(user.id);
        setInviteeFullName(user.fullName);
        setShowConfirmInviteForm(true);
    };

    const handleUserClick = (userId) => {
        setProfileId(userId);
        setShowProfileView(true);
    };

    const closeProfile = () => {
        setShowProfileView(false);
    };

    const closeConfirmInvite = () => {
        setShowConfirmInviteForm(false);
    };

    const handleUpgradePlan = () => {
        props.closePopup();

        localStorage.setItem(
            'view',
            JSON.stringify({
                subType: 'billing',
                type: 'userManagement'
            })
        );

        window.dispatchEvent(new Event('storage'));
    };

    const isUserDisabled = (user) => {
        return (
            isInvited.includes(user.id) ||
            handleDisable(user.id) ||
            (KZQ_ENABLE_BILLING && seatDetails.available === 0)
        );
    };

    const renderSearchResults = () => {
        if (showSearchUser) {
            return (
                <div className="inviteUsersView__empty">
                    <div className="inviteUsersView__emptyIcon">
                        <FiSearch />
                    </div>

                    <h4>Find an agent</h4>

                    <p>
                        Search by name, username, or email to find
                        someone to invite.
                    </p>
                </div>
            );
        }

        if (!userList || userList.length === 0) {
            return (
                <div className="inviteUsersView__empty">
                    <div className="inviteUsersView__emptyIcon inviteUsersView__emptyIcon--muted">
                        <FiUsers />
                    </div>

                    <h4>No agents found</h4>

                    <p>
                        Try searching with a different name, username,
                        or email address.
                    </p>
                </div>
            );
        }

        return (
            <div className="inviteUsersView__resultsList">
                {userList.map((user) => {
                    const disabled = isUserDisabled(user);

                    return (
                        <div
                            className={`inviteUsersView__userCard ${
                                disabled
                                    ? 'inviteUsersView__userCard--disabled'
                                    : ''
                            }`}
                            key={user.id}
                        >
                            <button
                                type="button"
                                className="inviteUsersView__userIdentity"
                                onClick={() =>
                                    handleUserClick(user.id)
                                }
                            >
                                <div className="inviteUsersView__avatar">
                                    {user.fullName
                                        ?.charAt(0)
                                        ?.toUpperCase() || 'U'}
                                </div>

                                <div className="inviteUsersView__userInfo">
                                    <div className="inviteUsersView__userName">
                                        {user.fullName || 'Unnamed user'}
                                    </div>

                                    <div className="inviteUsersView__userMeta">
                                        @{user.userName}
                                    </div>
                                </div>
                            </button>

                            <div className="inviteUsersView__userAction">
                                {disabled ? (
                                    <span className="inviteUsersView__status">
                                        <HiOutlineCheckCircle />
                                        Already added
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="inviteUsersView__inviteButton"
                                        onClick={() =>
                                            handleInvite(user)
                                        }
                                    >
                                        <FiUserPlus />
                                        Invite
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            aria-labelledby="invite-users-modal-title"
            className="inviteUsersView__modal"
        >
            <div className="inviteUsersView">
                <div className="inviteUsersView__container">

                    {/* HEADER */}
                    <header className="inviteUsersView__header">
                        <div className="inviteUsersView__headerBrand">
                            <div className="inviteUsersView__headerIcon">
                                <HiOutlineUserGroup />
                            </div>

                            <div>
                                <h2 id="invite-users-modal-title">
                                    Invite agents
                                </h2>

                                <p>
                                    Add people to{' '}
                                    <strong>
                                        {props.org?.orgName || 'your organization'}
                                    </strong>
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="inviteUsersView__close"
                            onClick={props.closePopup}
                            disabled={loading || isCheckingSeats}
                            aria-label="Close"
                        >
                            <FiX />
                        </button>
                    </header>

                    {/* CONTENT */}
                    <main className="inviteUsersView__content">

                        {isCheckingSeats ? (
                            <div className="inviteUsersView__loading">
                                <div className="inviteUsersView__spinner">
                                    <CircularProgress size={24} />
                                </div>

                                <h3>Checking availability</h3>

                                <p>
                                    Checking available seats for your
                                    organization...
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* SEAT CARD */}
                                {KZQ_ENABLE_BILLING && (
                                    <div className="inviteUsersView__seatCard">
                                        <div className="inviteUsersView__seatTop">
                                            <div>
                                                <span className="inviteUsersView__eyebrow">
                                                    CURRENT PLAN
                                                </span>

                                                <h3>
                                                    {currentPlan || 'No Plan'}
                                                </h3>
                                            </div>

                                            <div className="inviteUsersView__seatIcon">
                                                <HiOutlineUserGroup />
                                            </div>
                                        </div>

                                        <div className="inviteUsersView__seatStats">
                                            <div>
                                                <span>Total</span>
                                                <strong>
                                                    {seatDetails.activeSeat || 0}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Used</span>
                                                <strong>
                                                    {seatDetails.usedSeat || 0}
                                                </strong>
                                            </div>

                                            <div className="inviteUsersView__seatAvailable">
                                                <span>Available</span>
                                                <strong>
                                                    {seatDetails.available === -1
                                                        ? '∞'
                                                        : seatDetails.available || 0}
                                                </strong>
                                            </div>
                                        </div>

                                        {seatDetails.available !== -1 &&
                                            seatDetails.available < 3 &&
                                            seatDetails.available > 0 && (
                                                <div className="inviteUsersView__seatWarning">
                                                    <HiOutlineExclamationCircle />
                                                    Only {seatDetails.available}{' '}
                                                    seat
                                                    {seatDetails.available > 1
                                                        ? 's'
                                                        : ''}{' '}
                                                    remaining
                                                </div>
                                            )}
                                    </div>
                                )}

                                {!KZQ_ENABLE_BILLING ||
                                hasAvailableSeats ? (
                                    <section className="inviteUsersView__searchSection">

                                        <div className="inviteUsersView__sectionHeading">
                                            <div>
                                                <span className="inviteUsersView__step">
                                                    STEP 1
                                                </span>

                                                <h3>
                                                    Find an agent
                                                </h3>

                                                <p>
                                                    Search your user directory
                                                    and send an invitation.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="inviteUsersView__searchBox">
                                            <div className="inviteUsersView__searchInput">
                                                <TextField
                                                    placeholder="Search by name, username or email"
                                                    value={searchQuery}
                                                    onChange={
                                                        handleSearchQueryChange
                                                    }
                                                    onKeyDown={
                                                        onSearchKeyPress
                                                    }
                                                    fullWidth
                                                    variant="outlined"
                                                    disabled={loading}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <FiSearch />
                                                            </InputAdornment>
                                                        ),
                                                        endAdornment:
                                                            loading ? (
                                                                <InputAdornment position="end">
                                                                    <CircularProgress
                                                                        size={
                                                                            18
                                                                        }
                                                                    />
                                                                </InputAdornment>
                                                            ) : null
                                                    }}
                                                />
                                            </div>

                                            <button
                                                type="button"
                                                className="inviteUsersView__searchButton"
                                                onClick={handleSearch}
                                                disabled={
                                                    loading ||
                                                    !searchQuery.trim()
                                                }
                                            >
                                                {loading
                                                    ? 'Searching'
                                                    : 'Search'}
                                            </button>
                                        </div>

                                        <div className="inviteUsersView__resultsHeader">
                                            <span>
                                                {showSearchUser
                                                    ? 'Search results'
                                                    : `${userList?.length || 0} agents found`}
                                            </span>
                                        </div>

                                        <div className="inviteUsersView__results">
                                            {renderSearchResults()}
                                        </div>
                                    </section>
                                ) : (
                                    <section className="inviteUsersView__upgrade">

                                        <div className="inviteUsersView__upgradeHero">
                                            <div className="inviteUsersView__upgradeIcon">
                                                <HiOutlineSparkles />
                                            </div>

                                            <span className="inviteUsersView__step">
                                                SEATS FULL
                                            </span>

                                            <h3>
                                                Add more room for your team
                                            </h3>

                                            <p>
                                                You've reached the seat limit
                                                on your current plan. Choose
                                                an option below to keep growing
                                                your team.
                                            </p>
                                        </div>

                                        <div className="inviteUsersView__upgradeGrid">

                                            <div className="inviteUsersView__upgradeCard">
                                                <div className="inviteUsersView__upgradeCardIcon">
                                                    <HiOutlineSparkles />
                                                </div>

                                                <h4>
                                                    Upgrade your plan
                                                </h4>

                                                <p>
                                                    Move to a higher plan and
                                                    unlock more seats plus
                                                    additional features.
                                                </p>

                                                <ul>
                                                    <li>
                                                        More user seats
                                                    </li>
                                                    <li>
                                                        Advanced features
                                                    </li>
                                                    <li>
                                                        Priority support
                                                    </li>
                                                </ul>
                                            </div>

                                            <div className="inviteUsersView__upgradeCard">
                                                <div className="inviteUsersView__upgradeCardIcon">
                                                    <HiOutlineCreditCard />
                                                </div>

                                                <h4>
                                                    Buy additional seats
                                                </h4>

                                                <p>
                                                    Keep your existing plan and
                                                    purchase only the seats you
                                                    need.
                                                </p>

                                                <ul>
                                                    <li>
                                                        Keep current features
                                                    </li>
                                                    <li>
                                                        Pay for what you need
                                                    </li>
                                                    <li>
                                                        Flexible management
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        {props.org?.paymentType ===
                                        'automatic' ? (
                                            <div className="inviteUsersView__upgradeActions">
                                                <button
                                                    type="button"
                                                    className="inviteUsersView__primaryButton"
                                                    onClick={
                                                        handleUpgradePlan
                                                    }
                                                >
                                                    <HiOutlineCreditCard />
                                                    Go to billing
                                                </button>

                                                <button
                                                    type="button"
                                                    className="inviteUsersView__secondaryButton"
                                                    onClick={
                                                        props.closePopup
                                                    }
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="inviteUsersView__manualBilling">
                                                <HiOutlineExclamationCircle />

                                                <div>
                                                    <strong>
                                                        Contact your
                                                        administrator
                                                    </strong>

                                                    <p>
                                                        This organization uses
                                                        manual billing. Ask your
                                                        administrator to add
                                                        more seats.
                                                    </p>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="inviteUsersView__secondaryButton"
                                                    onClick={
                                                        props.closePopup
                                                    }
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        )}
                                    </section>
                                )}
                            </>
                        )}
                    </main>
                </div>

                <SearchUserProfileView
                    open={showProfileView}
                    closePopup={closeProfile}
                    profileId={profileId}
                />

                {showConfirmInviteForm && (
                    <ConfirmInviteForm
                        open={showConfirmInviteForm}
                        closePopup={closeConfirmInvite}
                        inviterId={activeUserId}
                        org={props.org}
                        project={currentProject}
                        team={props.team}
                        inviteType={props.inviteType}
                        inviteeId={inviteeId}
                        inviteeFullName={inviteeFullName}
                        reloadData={renderUserSeatDetails}
                    />
                )}
            </div>
        </Modal>
    );
}
